import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { capitalCase } from "change-case";
import { get, size } from "lodash";
import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";

import IconSpinner from "components/IconSpinner";
import IconTable, { type IconTableColumn } from "components/IconTable";
import { getEmployerContributionAbility } from "services/employerService";
import { kvPairs } from "utils/typeUtils";
import IconTableHeader from "components/IconTableHeader";

interface OwnProps {
  // PropTypes had companyId as PropTypes.string (not .isRequired) → optional
  companyId?: string;
}

type Props = WithApolloClient<OwnProps>;

// getEmployerContributionAbility resolves to ContributionAbility | object; the
// component reads fields via lodash `get`, so a loose response type suffices.
type ContributionAbilityResponse = Awaited<
  ReturnType<typeof getEmployerContributionAbility>
>;

// KvPair is not exported from typeUtils; derive the row shape from kvPairs.
type KvPairRow = ReturnType<typeof kvPairs>[number];

const EntityPayrollDetails = ({ client, companyId }: Props) => {
  const [contributionAbility, setContributionAbility] =
    useState<ContributionAbilityResponse>();
  const [isInitialFetch, setIsInitialFetch] = useState(true);

  useEffect(() => {
    const getContributionAbility = async () => {
      const response = await getEmployerContributionAbility(
        // client is optional in WithApolloClient; service requires it
        client as ApolloClient<object>,
        // companyId is optional per PropTypes; service requires a string
        companyId as string
      );
      setContributionAbility(response);
    };
    // found-not-fixed: this guard only fetches when !isInitialFetch, but
    // isInitialFetch starts `true` and is only cleared inside this branch — so
    // the fetch never runs and the spinner below never clears. Preserved
    // verbatim from master (identical logic); a real fix needs product/QA
    // sign-off (likely the guard should be inverted or the flag seeded false).
    if (!isInitialFetch) {
      void getContributionAbility().finally(() => {
        setIsInitialFetch(false);
      });
    }
  }, [isInitialFetch]);

  const tableMetadata: IconTableColumn<KvPairRow>[] = [
    {
      key: "key",
      label: "Key",
      format: (value) => {
        return capitalCase(value as string);
      },
    },
    {
      key: "value",
      label: "Value",
      format: (v) => {
        return `${String(v)}`;
      },
    },
  ];

  if (isInitialFetch) {
    return <IconSpinner centered />;
  }

  const sections = {
    currentYear: kvPairs(get(contributionAbility, "currentYear", {})),
    priorYear: kvPairs(get(contributionAbility, "priorYear", null)),
  };
  const other = kvPairs({
    isWithinPayrollPriorYearContributionWindow: get(
      contributionAbility,
      "isWithinPayrollPriorYearContributionWindow",
      false
    ),
    isWithinPriorYearContributionWindow: get(
      contributionAbility,
      "isWithinPriorYearContributionWindow",
      false
    ),
    payrollPriorYearCutoffDate: get(
      contributionAbility,
      "payrollPriorYearCutoffDate",
      ""
    ),
    priorYearCutoffDate: get(contributionAbility, "priorYearCutoffDate", ""),
  });

  return (
    <>
      <h1 className="page-title">Contribution Ability</h1>
      <Card>
        <div>
          {(Object.keys(sections) as Array<keyof typeof sections>)
            .filter((section) => size(sections[section]) > 0)
            .map((section, index) => {
              const data = sections[section];
              return (
                <Card key={index}>
                  <IconTableHeader
                    tableCount={size(data)}
                    tableHeader={capitalCase(section)}
                  />
                  <IconTable columns={tableMetadata} data={data} />
                </Card>
              );
            })}
        </div>
        <div>
          <Card key="other">
            <IconTableHeader tableCount={size(other)} tableHeader="Other" />
            <IconTable columns={tableMetadata} data={other} />
          </Card>
        </div>
      </Card>
    </>
  );
};

export default withApollo<Props>(EntityPayrollDetails);
